// Per GTM Analytics standards the objects created should use a single quote surrounding properties
'use strict';

import _ from 'underscore';
import $ from 'jquery';
import utilities from '../../js/utilities';
import $global from '../../js/globalElements';
import mediator from '../../js/mediator';
import config from '../../js/config';
import analyticsCart from './AnalyticsCart';
import cookies from 'js-cookie';
import { EnableAdobeAnalytics, EnableB2BUsersApiCall } from '../../utilities/featureFlags';
import cartApi from '../../services/CartAPI';

window.dataLayer = window.dataLayer || [];

const MEDIATOR_EVENTS = [
  {listenTo: config.events.cart.added, label: config.events.cart.added},
  {listenTo: config.events.cart.failedToAdd, label: config.events.cart.failedToAdd}
];

// This object is auto-initialized and included in WWWEntryPoints.js
class Analytics {
  /**
   * This method is meant for checking to see if global data objects have been injected on the page.
   *
   * @param {object} config - The main site config
   */
  initialize(config) {

    if (EnableAdobeAnalytics.isEnabled()) {
      window.adobeDataLayer = window.adobeDataLayer || [];
    } else {
      window.dataLayer = window.dataLayer || [];
    }

    this.config = config;

    // Using capitalization to give impression that these are constants and should not be changed
    this.MEDIATOR_TAG = this.config.analytics.attributes.optional.mediator;
    this.GTM_ATTRIBUTES = Object.keys(this.config.analytics.attributes.mandatory).map(key => {
      return this.config.analytics.attributes.mandatory[key];
    });
    this.MEDIATOR_EVENTS = MEDIATOR_EVENTS;
    this.EVENT_TAG = this.config.analytics.attributes.mandatory.event;
    this.LABEL_TAG = this.config.analytics.attributes.mandatory.label;
    this.CATEGORY_TAG = this.config.analytics.attributes.mandatory.category;
    this.OFF_TAG = this.config.analytics.attributes.optional.offTag;
    this.TAG_PREFIX = this.config.analytics.attributes.utility.tagPrefix;
    this.SEPARATOR = this.config.analytics.attributes.utility.separator;
    if (window.AnalyticsObjects) {
      this._checkForGenericAnalyticsObject();
    }
    this._attachListeners();
    this._checkAnalyticsQueue();
    this.setPageTypeAndUTMFieldsOnDataLayer()
  }

  /**
   * Push an generic event to the data layer based on
   * the event framework structure in GTM
   *
   * @param {object} data
   * @param {string} data.event
   * @param {string} data.category
   * @param {string} data.action
   * @param {string|number} data.label
   * @param {number} data.value
   */
  pushToEventFramework(data) {
    let {event, ...otherData} = data;
    let {category, action, label, ...eventData} = otherData;
    window.dataLayer.push({
      event,
      eventframework: {
        category,
        action,
        label,
        ...eventData
      }
    });
  }
  adobeAccountData(emailId, type) {
    let accountDataToPass;
    switch (type) {
      case 'account_login':
        accountDataToPass = {
          event: type,
          accountID: emailId || undefined
        };
        break;
      case 'account_CreateStart':
        accountDataToPass = {
          event: type
        }
        break;
      case 'account_CreateComplete':
        accountDataToPass = {
          event: type,
          accountID: emailId || undefined
        }
        break;
      default:
        break;
    }
    this.setOnAdobeDataLayer(accountDataToPass);
  }
  pushFormInteractionData(type, formName) {
    let formDatatoPass = {
      'event': type,
      'formName': formName
    }
    this.setOnAdobeDataLayer(formDatatoPass);
  }
  pushFormErrorData(formName, errorMessages) {
    let formDatatoPass = {
      'event': 'form_error',
      'formName': formName,
      'error_messages':  errorMessages || undefined
    }
    this.setOnAdobeDataLayer(formDatatoPass);
  }
  pushDataToEventFramework(data) {
    window.adobeDataLayer.push({
      event: config.analytics.eventTypes.LinkClick || undefined,
      contentType: data.gtmContenttype || undefined,
      linkUrl: data.gtmLinkurl || undefined,
      name: data.gtmLinktext || undefined,
      region: data.gtmRegion || undefined,
      type: data.gtmDynamictype || undefined
    });
    if ((data.gtmRegion === "Datasheets" || data.gtmRegion === "Products") && data.gtmLinktext !== 'Download') {
      this.sendDatasheetInfoToAdobe(data);
    }
    if (data.gtmRegion === "ResearchAndEvents") {
      this.sendFeaturedArticlesInfoToAdobe(data);
    }
  }
  sendDatasheetInfoToAdobe(data) {
    window.adobeDataLayer.push({
      event: config.analytics.eventTypes.DatasheetInteraction || undefined,
      dsCategory: data.gtmDscategory || undefined,
      dsAssociatedProducts: data.gtmDsassociatedproducts ? data.gtmDsassociatedproducts.split(',') : undefined,
      dsFamily: data.gtmDatasheetfamily || undefined
    });
  }
  sendFeaturedArticlesInfoToAdobe(data) {
    window.adobeDataLayer.push({
      event: 'article_interaction',
      articleTitle: data.gtmLabel,
      articleTags: undefined
    });
  }
  /**
   * Push an generic event to the data layer based on
   * the event framework structure in GTM
   *
   * @param {object} data
   * @param {string} data.event
   * @param {object} data.value
   */
  pushToECommercePromotionalEventFramework(data) {
    let {event, ...otherData} = data;
    let {id, name, creative, position} = otherData;
    window.dataLayer.push({
      event,
      ecommerce: {
        promoClick: {
          promotions: [{
            id,
            name,
            creative,
            position
          }]
        }
      }
    });
  }

  adobeInternalCampaignClick(bannerName) {
    const campaignClickData = {
      event: 'internal_campaign_click',
      campaignName: bannerName || undefined
    }
    this.setOnAdobeDataLayer(campaignClickData);
  }

  adobeInternalCampaignView(bannerName) {
    window.adobeDataLayer = window.adobeDataLayer || [];
    const campaignViewData = {
      event: 'internal_campaign_impression',
      campaignName: bannerName || undefined
    }
    this.setOnAdobeDataLayer(campaignViewData);
  }

  /**
   * Push data to analytics queue and invoke method
   * to check whether sufficient chunks are available.
   *
   * @param {object} data
   */
  pushToAnalyticsQueue(data) {
    if (window && window.analyticsQueue && data) {
      window.analyticsQueue.push(data);
      this._checkAnalyticsQueue ();
    }
  }

  /**
   * Push to the data layer for the eventFramework
   *
   * @deprecated We are preferring to use the specific push methods as this was very generic
   * @param {string} event - The event
   * @param {string} category - The category
   * @param {string} action - The action
   * @param {string} label - The label
   * @param {object} [attrs] - Any additional properties
   */
  push(event, category, action, label, attrs) {
    this.pushToEventFramework({event, category, action, label, ...attrs});
  }

  async setPageTypeAndUTMFieldsOnDataLayer()
  {
    const cPageUrl = window.location.href.toString().split(window.location.host)[1];
    let pageTypeData = this.getPageTypeAnalyticsData(cPageUrl);
    var drParam = sessionStorage && sessionStorage.getItem('dr');
    var dlParam = sessionStorage && sessionStorage.getItem('dl');
    var hasVisitedBefore = sessionStorage && sessionStorage.getItem('hasVisitedBefore');

    if (!drParam && !hasVisitedBefore)
    {
      const dr = this.queryParam('dr');
      drParam = (dr ? dr : document.referrer);
    }

    if (!dlParam && !hasVisitedBefore)
    {
      const dl = this.queryParam('dl');
      dlParam = (dl ? dl : window.location.href);
    }

    if (sessionStorage)
    {
      sessionStorage.setItem('dl', dlParam);
      sessionStorage.setItem('dr', drParam);
      sessionStorage.setItem('hasVisitedBefore', 'true');
    }

    if (EnableAdobeAnalytics.isEnabled()) {
      let currentUser = utilities.sessionStorageLoad(this.config.sessionVariables.header.b2busers);
      if (window.Arrow.isAuthorized && EnableB2BUsersApiCall.isEnabled()) {
        currentUser = await cartApi.getLoyaltyUser();
      }
      console.log('\n\ncurrentUser=', currentUser, '\n\n');

      let personalInfo = {};
      if (utilities.sessionStorageLoad(this.config.sessionVariables.header.personalInfo)) {
        try {
          personalInfo = JSON.parse(utilities.sessionStorageLoad(this.config.sessionVariables.header.personalInfo));
        } catch (e) {
          personalInfo = {};
        }
      }
      const url = new URL(document.location.href);
      const adobePageInfo = this.getAdobePageInfo(url);
      const pageObject = {
        event: 'pageview',
        campaign: {
          utm_channel: url.searchParams.has('utm_channel') ? url.searchParams.get('utm_channel') : undefined,
          utm_medium: url.searchParams.has('utm_medium') ? url.searchParams.get('utm_medium') : undefined,
          utm_name: url.searchParams.has('utm_name') ? url.searchParams.get('utm_name') : undefined,
          utm_source: url.searchParams.has('utm_source') ? url.searchParams.get('utm_source') : undefined,
          utm_source_platform: url.searchParams.has('utm_source_platform') ? url.searchParams.get('utm_source_platform') : undefined,
          utm_strategy: url.searchParams.has('utm_strategy') ? url.searchParams.get('utm_strategy') : undefined
        },
        pageName: adobePageInfo.pageName,
        siteSection: adobePageInfo.siteSection,
        pageCategory: adobePageInfo.pageCategory,
        pageSubcategory: adobePageInfo.pageSubcategory,
        pagePath: window.location.href.split('?')[0].split(url.hostname)[1],
        pageType: pageTypeData,
        pageCleanUrl: window.location.href.split('?')[0],
        pageQuerystring: window.location.href.split('?')[1],
        pageHostname: url.hostname,
        userId: currentUser ? currentUser.uid : undefined,
        pageLanguage: cookies.get(this.config.cookies.arrowLanguage) || (personalInfo ? personalInfo.language : undefined),
        userState: window.Arrow.isAuthorized ? 'loggedIn' : 'loggedOut',
        userType: (currentUser && currentUser.arrowCustomerType) ? currentUser.arrowCustomerType : 'B2C'
      }
      this.setOnAdobeDataLayer(pageObject);
    } else {
      let pageObject = {
        page: {
          pageInfo: {
            pageType: pageTypeData ? pageTypeData : '',
            dr: drParam,
            dl: dlParam
          }
        }
      };
      this.setOnDataLayer(pageObject);
    }
  }

  /**
   * Get adobe page info data for adobe Analytic
   *
   * @param {string} url - The event
   * @return {object}
   */
  getAdobePageInfo(url) {

    let pathUrl = url.pathname.replace(/^(\/en\/|\/fr-fr\/|\/de-de\/|\/it-it\/|\/ko-kr\/|\/es-mx\/|\/zh-cn\/|\/|)/,'').replaceAll('-',' ');
    const domain = url.hostname.split('.')[1];

    if (pathUrl === '' || pathUrl === 'en' || pathUrl === 'fr-fr' || pathUrl === 'de-de' || pathUrl === 'it-it' || pathUrl === 'ko-kr' || pathUrl === 'es-mx' || pathUrl === 'zh-cn') pathUrl = 'home';
    const splitUrlParts = pathUrl.split('/');
    const pageName = [domain].concat(splitUrlParts).join(' | ');

    const siteSectionSplitUrlParts = pathUrl.split('/').slice(0, 2);
    const siteSection = [domain].concat(siteSectionSplitUrlParts).join(' | ');

    const pageCategory = (pathUrl === 'home') ? undefined : siteSectionSplitUrlParts[0];
    const pageSubcategory = (pathUrl === 'home') ? undefined : siteSectionSplitUrlParts[1];

    return { pageName, siteSection, pageCategory, pageSubcategory };
  }

  queryParam(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    var results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }

  /**
   * Set some properties onto the dataLayer so that
   * next event includes them
   *
   * @param {object} properties Any object to apply to the dataLayer
   */
  setOnDataLayer(properties) {
    window.dataLayer.push(properties);
  }

  /**
   * Set some properties onto the adobeDataLayer so that
   * next event includes them
   *
   * @param {object} properties Any object to apply to the adobeDataLayer
   */
  setOnAdobeDataLayer(properties) {
    window.adobeDataLayer.push(properties);
  }

  /**
   * Push to the Tealium framework.
   *
   * Ensures the properties are in correct order.
   *
   * @param {string} event - The event
   * @param {string} category - The category
   * @param {string} action - The action
   * @param {string} label - The label
   * @param {string} value - The value
   */
  pushTealium(event, category, action, label, value) {
    if (!window.utag) {
      return;
    }
    window.utag.link({event, category, action, label, value});
  }

  /**
   * This takes a sales order and flattens it for Tealium datalayer
   * @param salesOrder - Order returned from getOrderHistoryDetails
   */
  pushTealiumOrderConfirmation(salesOrder) {
    if (!window.utag) {
      return;
    }
    let tealiumOrder = {
      page_type: 'order-complete',
      page_language: cookies.get(config.cookies.arrowLanguage),
      currency_code: salesOrder.currencyCode,
      currency_divisor: config.locale.exchangeRate.toString(),
      order_id: salesOrder.salesOrderId,
      order_affiliation: salesOrder.orderSource,
      order_coupon: salesOrder.discountCode ? salesOrder.discountCode : '',
      payment_method: salesOrder.paymentMethodCode,
      shipping_method: this._getCarrierCodesWithDelimiter(salesOrder.shippingCarriers, '|'),
      order_total: salesOrder.total.toString(),
      order_total_before_discount: salesOrder.totalBeforeDiscount.toString(),
      discount_amount: salesOrder.totalDiscountAmount ? salesOrder.totalDiscountAmount.toString() : '0',
      order_subtotal: salesOrder.subtotal.toString(),
      order_tax: salesOrder.taxTotal.toString(),
      order_shipping: salesOrder.shippingFee.toString()
    };
    tealiumOrder = this._addProductDataForTealiumOrder(salesOrder, tealiumOrder);
    window.utag.view(tealiumOrder);
  }

  /**
   * Used to emit events from JS code when certain events occur.
   *
   * @param {object} event
   * @param {string} [event.event]
   * @param {string} event.category
   * @param {string} event.action
   * @param {string|number} event.label
   * @param {string|number} event.value
   */
  emitNewDataLayerEvent(event) {
    let data = {
      'event': event.event || this.config.analytics.triggers.genericEvent,
      'category': event.category,
      'action': event.action,
      'label': event.label,
      'value': event.value
    };
    this.pushToEventFramework(data);
    this.pushTealium(event.event || this.config.analytics.triggers.genericEvent, event.category, event.action, event.label, event.value);
  }

  /**
   * When a product has been added to the cart.
   *
   * @param {object} data
   * @param {AddedProduct[]} data.addedProducts - The product just added
   * @param {CartEntry[]} data.entries - Cart entries
   */
  handleProductAddedToCart(data) {
    if (EnableAdobeAnalytics.isEnabled()) {
      const productData = analyticsCart.getAddedItemDetailAdobeFormat(data);
      if (data.cartItemCount === 1 && data.addedProducts[0].lastCartState.totalItems === 0) {
        this.openCartAdobeAnalytic(data.guid, productData);
      }
      this.addToCartAdobeAnalytic(data.guid, productData);
    } else {

      let event = window.location.pathname.indexOf('/search') > -1 ? this.config.analytics.triggers.addToCartSearch : this.config.analytics.triggers.addToCart;
      data.event = event;

      let ecommerceData = {
        exchangeRate: this.config.locale.exchangeRate,
        add: {}
      };

      let list = analyticsCart.getActionField();

      if (list) {
        ecommerceData.add.actionField = {list, action: 'add'};
      }
      ecommerceData.add.products = analyticsCart.getAddedItemDetails(data);
      this.ecommercePushWithCurrency(event, ecommerceData);
    }
  }

  /**
   * Adobe Analytic : open cart
   *
   * @param {object} data
   */
  openCartAdobeAnalytic(cartId, data) {
    const eventData = {
      event: 'cart_open',
      cartId: cartId,
      products: data
    }
    this.setOnAdobeDataLayer(eventData);
  }

  /**
   * Adobe Analytic : add to cart
   *
   * @param {object} data
   */
  addToCartAdobeAnalytic(cartId, data) {
    const eventData = {
      event: 'add_to_cart',
      cartId: cartId,
      products: data
    }
    this.setOnAdobeDataLayer(eventData);
  }

  /**
   * Ecommerce push
   *
   * @param {string} event
   * @param {object} data
   */
  ecommercePushWithCurrency(event, data) {
    let ecommerceData = {
      currencyCode: config.locale.currencyIsoCode
    };
    Object.assign(ecommerceData, data);
    window.dataLayer.push({
      event,
      ecommerce: ecommerceData
    });
  }

  /**
   * Ecommerce push
   *
   * @param {string} event
   * @param {object} data
   */
  ecommerceDataPush(event, data) {
    window.dataLayer.push({
      event,
      ecommerce: data
    });
  }

  /**
   * Used to add all product data from salesorder and flatten to arrays
   * @param salesOrder
   * @param tealiumOrder
   * @returns {salesOrder}
   * @private
   */
  _addProductDataForTealiumOrder(salesOrder, tealiumOrder) {
    let lineItems = [];
    if (salesOrder.shipmentsWithUSOrigin) {
      lineItems = lineItems.concat(this._mergeAllPartsFromShipmentOrigin(salesOrder.shipmentsWithUSOrigin));
    }

    if (salesOrder.shipmentsWithNonUSOrigin) {
      lineItems = lineItems.concat(this._mergeAllPartsFromShipmentOrigin(salesOrder.shipmentsWithNonUSOrigin));
    }

    tealiumOrder.product_name = [];
    tealiumOrder.product_id = [];
    tealiumOrder.product_brand = [];
    tealiumOrder.product_quantity = [];
    tealiumOrder.product_price = [];
    tealiumOrder.product_line_price = [];

    for (let i = 0; i < lineItems.length; i++) {
      tealiumOrder.product_name.push(lineItems[i].mpn);
      tealiumOrder.product_id.push(lineItems[i].catalogPartId);
      tealiumOrder.product_brand.push(lineItems[i].manufacturer);
      tealiumOrder.product_quantity.push(lineItems[i].quantity.toString());
      tealiumOrder.product_price.push(lineItems[i].unitPrice.toString());
      tealiumOrder.product_line_price.push(lineItems[i].linePrice.toString());
    }

    return tealiumOrder;
  }

  /**
   * Merges the parts from each origin that a property that has an array of shipments
   * @param shipmentsWithOrigin
   * @returns {Array}
   * @private
   */
  _mergeAllPartsFromShipmentOrigin(shipmentsWithOrigin) {
    let parts = [];
    // eslint-disable-next-line no-unused-vars
    for (let prop in shipmentsWithOrigin) {
      if (shipmentsWithOrigin.hasOwnProperty(prop)) {
        for (let i = 0; i < shipmentsWithOrigin[prop].length; i++) {
          parts.push(shipmentsWithOrigin[prop][i]);
        }
      }
    }
    return parts;
  }

  /**
   * Gets all carriers from salesOrder.shippingCarriers and makes the codes on string delimited by what's sent in
   * @param shippingCarriers
   * @param delimiter
   * @returns {string}
   * @private
   */
  _getCarrierCodesWithDelimiter(shippingCarriers, delimiter) {
    let carriersString = '';
    for (let i = 0; i < shippingCarriers.length; i++) {
      carriersString = `${carriersString}${shippingCarriers[i].carrierCode}${delimiter}`;
    }

    carriersString = carriersString.substring(0, carriersString.length - 1);
    return carriersString;
  }

  _attachListeners() {
    this._setupAnalyticClickEvents();
    this._setupAnalyticMediatorEvents();
    this._checkForGenericAnalyticEvents();
    //Commenting out for now for rc/2016.0315.842. there are issues when data does not come back from the cart causing js issues.
    //This has been fixed in master with work on: https://arrowecommerce.atlassian.net/browse/ARW-756
    mediator.on(this.config.events.cart.added, this.handleProductAddedToCart, this);
    mediator.on(this.config.events.ui.ajaxContentLoaded, this._checkAnalyticsQueue, this);
  }

  /**
   * Searches for elements that contain MEDIATOR_TAG, when value is broadcast will push data to dataLayer (eg, when an element is visible on hover).
   *
   * @private
   */
  _checkForGenericAnalyticEvents() {
    $global.$body.find('[' + this.MEDIATOR_TAG + ']').each((i, el) => {
      let gtmObj = {};
      this.GTM_ATTRIBUTES.forEach((attr) => {
        gtmObj[attr.replace(this.TAG_PREFIX, '')] = el.attributes[attr].value;
      });
      mediator.on(el.attributes[this.MEDIATOR_TAG].value, () => {
        this.pushToEventFramework(gtmObj);
      });
    });
  }

  /**
   * Will push whatever is emitted from a mediator event stored in MEDIATOR_EVENTS, straight into the data layer.
   *
   * @private
   */
  _setupAnalyticMediatorEvents() {
    this.MEDIATOR_EVENTS.forEach((attr) => {
      mediator.on(attr.listenTo, (data) => {
        if (_.isUndefined(data) || data === null) return;
        let obj = {};
        obj[attr.label] = data;
        this.pushToEventFramework(obj);
      });
    });
  }

  _setupAnalyticClickEvents() {
    $global.$body.on('click', 'a[' + this.EVENT_TAG + '], button[' + this.EVENT_TAG + '], .gtm-track[' + this.EVENT_TAG + ']', (e) => {
      this._basicPush(e);
    });
    $global.$body.on('change', 'input:radio[' + this.EVENT_TAG + ']', (e) => {
      this._basicPush(e);
    });
    $global.$body.on('change', 'input:checkbox[' + this.OFF_TAG + ']', (e) => {
      this._checkboxEvent(e);
    });
    $global.$body.on('change', 'select[' + this.EVENT_TAG + ']', (e) => {
      this._basicPush(e);
    });
  }

  _basicPush(e) {
    if (EnableAdobeAnalytics.isEnabled()) {
      this.pushDataToEventFramework(e.currentTarget.dataset);
    } else {
      this.pushToEventFramework(this._createAnalyticObject(e.currentTarget.dataset));
    }
  }

  _checkboxEvent(e) {
    this.pushToEventFramework(!e.currentTarget.dataset.gtmActionOff ? this._createAnalyticObject(e.currentTarget.dataset) : this._createToggledObject(e));
  }

  _createToggledObject(e) {
    let data = e.currentTarget.dataset;
    let parsedData = this._createAnalyticObject(data);
    parsedData.action = !e.currentTarget.checked ? data.gtmActionOff : data.gtmAction;
    return parsedData;
  }

  /**
   * Create a analytic object from an elements dataset.
   *
   * @param {HTMLElement.dataset} data
   * @returns {{event: string, category: string, action: string, label: string}}
   * @private
   */
  _createAnalyticObject(data) {
    return {
      event: data.gtmEvent,
      category: data.gtmCategory,
      action: data.gtmAction,
      label: data.gtmLabel
    };
  }

  getPageTypeAnalyticsData(currentPageUrl) {
    if (currentPageUrl) {
      switch (true) {
        case (new RegExp('^/$|^/(..(-..)?(/)?)?(\\?.+)?$').test(currentPageUrl)) :
          return 'Home Pages';
          break;
        case (new RegExp('/products/.+/.+').test(currentPageUrl)):
          return 'Product Detail';
          break;
        case (new RegExp('/products(/)?(\\?.+)?$').test(currentPageUrl)):
          return 'Products Landing';
          break;
        case currentPageUrl.indexOf('/manufacturers') > -1:
          return 'Manufacturers';
          break;
        case currentPageUrl.indexOf('/category') > -1:
        case currentPageUrl.indexOf('/categories') > -1:
          const $categoryItem = $('.Part-Category');
          let categoryTitle = 'Product Category';
          if ($categoryItem && $categoryItem.data('category-hierarchy')) {
            const hierarchyArray = $categoryItem.data('category-hierarchy').split('/');
            if ($categoryItem && $categoryItem.length > 0 && hierarchyArray.length > 1) {
              categoryTitle = 'Product Subcategory'
            }
          }
          else {
            categoryTitle = 'Custom Category Page';
          }
          return categoryTitle;
          break;
        case currentPageUrl.indexOf('/product-category-sitemap') > -1:
          return 'Product Category';
          break;
        case currentPageUrl.indexOf('/products/search') > -1 && currentPageUrl.indexOf('datasheets=true') === -1:
          return 'Product Search';
          break;
        case currentPageUrl.indexOf('/products/search') > -1 && currentPageUrl.indexOf('datasheets=true') > -1:
          return 'Datasheet Search';
          break;
        case (new RegExp('/datasheets/.+').test(currentPageUrl)):
          return 'Datasheet Detail';
          break;
        case currentPageUrl.indexOf('/datasheets') > -1:
          return 'Datasheets Landing';
          break;
        case (new RegExp('/research-and-events(/)?(\\?.+)?$').test(currentPageUrl)) :
          return 'Research and Events';
          break;
        case (new RegExp('/research-and-events/articles(/)?(\\?.+)?$').test(currentPageUrl)) :
          return 'Articles Landing';
          break;
        case (new RegExp('/research-and-events/videos(/)?(\\?.+)?$').test(currentPageUrl)) :
          return 'Videos Landing';
          break;
        case (new RegExp('/research-and-events/events(/)?(\\?.+)?$').test(currentPageUrl)) :
          return 'Events Landing';
          break;
        case (new RegExp('/reference-designs(/)?(\\?.+)?$').test(currentPageUrl)) :
          return 'Reference Designs Landing';
          break;
        case (new RegExp('/reference-designs/.+/.+').test(currentPageUrl)) :
          return 'Reference Designs Detail';
          break;
        case (currentPageUrl.indexOf('/reference-designs/search') > -1) :
          return 'Reference Designs Search';
          break;
        case (currentPageUrl.indexOf('/support') > -1) :
          return 'Support';
          break;
        case (currentPageUrl.indexOf('/login') > -1) :
        case (currentPageUrl.indexOf('/register') > -1) :
          return 'Login-Register';
          break;
        case currentPageUrl.indexOf('/design-center') > -1:
          return 'Design Center';
          break;
        case currentPageUrl.indexOf('/bom-tool') > -1:
          return 'BOM Tool';
          break;
        // Handled in ArticlesEventsMarketingTracing.js
        case (currentPageUrl.indexOf('/articles/') > -1 || currentPageUrl.indexOf('/videos/') > -1  || currentPageUrl.indexOf('/events/') > -1):
          break;
        default:
          return 'Other';
      }
    }
  }

  /**
   * Way to dynamically set data-gtm- attributes in JS if they don't already exist and without relying on HBS/other templating.
   *
   * @example
   * this.setAttributes($global.$body.find('span').eq(0), {event: 'Click', action: 'Click', label: 'A thing we need to track', category: 'Meganav'});
   *
   * @param {HTMLElement|jQuery} element
   * @param {object} attributes
   * @param {string} attributes.event
   * @param {string} attributes.category
   * @param {string} attributes.action
   * @param {string|number} attributes.label
   */
  setAttributes(element, attributes) {
    element = utilities.returnVanillaElement(element);
    // eslint-disable-next-line no-unused-vars
    for (let attribute in attributes) {
      element.setAttribute(this.TAG_PREFIX + attribute, attributes[attribute]);
    }
  }

  /**
   * Set a data-gtm-label attribute on the element with the given value.
   *
   * @param {HTMLElement|jQuery} element
   * @param {string} labelAttribute
   */
  setLabelAttribute(element, labelAttribute) {
    element = utilities.returnVanillaElement(element);
    element.setAttribute(this.LABEL_TAG, labelAttribute);
  }

  _checkForGenericAnalyticsObject() {
    if (window.AnalyticsObjects.genericAnalyticsData) {
      this.pushToEventFramework(window.AnalyticsObjects.genericAnalyticsData);
    }
  }

  /**
   * Check the analytics queue to see if there is any data that needs acting on.
   *
   * Currently only supports chunked datalayer pushes.
   *
   * @example
   *   window.analyticsQueue.push({
   *    id: 'uniqueIdForTheseChunks',
   *    options: { expectedChunks: 2 },
   *    data: {
   *      ecommerce: {
   *        detail: {
   *          products: [{ price: 9.99, dimension14: 12 }]
   *        }
   *      }
   *    }
   *  });
   *
   * @private
   */
  _checkAnalyticsQueue() {
    if (window && window.analyticsQueue) {
      _.chain(window.analyticsQueue)
        .groupBy('id')
        .forEach(items => {
          let maxExpectedChunkCount = _.max(_.map(items, item => (item.options && item.options.expectedChunks) || 1));

          // If we now have all the required chunks
          if (maxExpectedChunkCount === items.length) {
            // Merge and track the data
            let item = $.extend(true, {}, ...items);
            window.dataLayer.push(item.data);

            // Remove the items from the queue now we are done with them
            window.analyticsQueue = _.without(window.analyticsQueue, ...items)
          }
        });
    }
  }
  adobeCarousalImageImp(imageName) {
    window.adobeDataLayer = window.adobeDataLayer || [];
    let carousalImageNames = [];
    carousalImageNames.push(imageName);
    window.adobeDataLayer.push({
      event: 'carousel_image_impression',
      imageName: carousalImageNames.length === 0 ? undefined : carousalImageNames
    });
  }
  adobeCarousalMultipleImageImp(imageName) {
    window.adobeDataLayer = window.adobeDataLayer || [];
    if (imageName.length > 0) {
      window.adobeDataLayer.push({
        event: 'carousel_image_impression',
        imageName: imageName.length === 0 ? undefined : imageName
      });
    }
  }
}

module.exports = new Analytics();



// WEBPACK FOOTER //
// ./assets/components/Analytics/Analytics.js